import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { User, Buildings, FlowArrow, Cpu, Target, ListChecks, ArrowsLeftRight, ArrowSquareOut, BookOpen, Warning, CaretRight } from "@phosphor-icons/react";

export default function DetailSheet({ open, onOpenChange, item, roles, departments, systems }) {
  if (!item) return null;

  const { type, data } = item;

  const renderRoleDetails = () => (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="w-full bg-secondary/50">
        <TabsTrigger value="overview" className="flex-1" data-testid="role-tab-overview">Overview</TabsTrigger>
        <TabsTrigger value="kpis" className="flex-1" data-testid="role-tab-kpis">KPIs/KRAs</TabsTrigger>
        <TabsTrigger value="flows" className="flex-1" data-testid="role-tab-flows">Flows</TabsTrigger>
        <TabsTrigger value="training" className="flex-1" data-testid="role-tab-training">Training</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="mt-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Purpose</h4>
            <p className="text-sm text-foreground">{data.purpose || "No purpose defined"}</p>
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Responsibilities</h4>
            {data.responsibilities?.length > 0 ? (
              <ul className="space-y-1">
                {data.responsibilities.map((r, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CaretRight size={14} className="text-primary mt-1 flex-shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No responsibilities listed</p>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Reports To</h4>
            {data.reports_to ? (
              <Badge variant="outline">{roles.find(r => r.role_id === data.reports_to)?.title || data.reports_to}</Badge>
            ) : (
              <Badge variant="secondary">Top Level</Badge>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Software Used</h4>
            <div className="flex flex-wrap gap-2">
              {data.software_used?.length > 0 ? (
                data.software_used.map((sw, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-red-50 text-red-700 border-red-200">{sw}</Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No software listed</p>
              )}
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="kpis" className="mt-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
              <Target size={14} /> Key Result Areas (KRAs)
            </h4>
            {data.kras?.length > 0 ? (
              <ul className="space-y-2">
                {data.kras.map((kra, idx) => (
                  <li key={idx} className="p-2 bg-primary/5 rounded-md text-sm">{kra}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No KRAs defined</p>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
              <ListChecks size={14} /> Key Performance Indicators (KPIs)
            </h4>
            {data.kpis?.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {data.kpis.map((kpi, idx) => (
                  <div key={idx} className="p-3 bg-accent/10 rounded-md border border-accent/20">
                    <p className="text-xs text-muted-foreground">{kpi.name}</p>
                    <p className="text-lg font-bold text-accent-foreground">{kpi.target}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No KPIs defined</p>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="flows" className="mt-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Inputs Received</h4>
            {data.inputs_received?.length > 0 ? (
              <ul className="space-y-1">
                {data.inputs_received.map((input, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {input}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No inputs listed</p>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Outputs Produced</h4>
            {data.outputs_produced?.length > 0 ? (
              <ul className="space-y-1">
                {data.outputs_produced.map((output, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    {output}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No outputs listed</p>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
              <ArrowsLeftRight size={14} /> Handoffs
            </h4>
            {data.handoffs?.length > 0 ? (
              <div className="space-y-2">
                {data.handoffs.map((ho, idx) => (
                  <div key={idx} className="p-2 bg-secondary rounded-md flex items-center gap-2">
                    <span className="text-sm font-medium">{ho.to}</span>
                    <span className="text-xs text-muted-foreground">→ {ho.what}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No handoffs defined</p>
            )}
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Dependencies</h4>
            <div className="flex flex-wrap gap-2">
              {data.dependencies?.length > 0 ? (
                data.dependencies.map((dep, idx) => (
                  <Badge key={idx} variant="outline">{dep}</Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No dependencies listed</p>
              )}
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="training" className="mt-4">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
            <BookOpen size={14} /> Training Assets & SOPs
          </h4>
          {data.training_assets?.length > 0 ? (
            <div className="space-y-2">
              {data.training_assets.map((asset, idx) => (
                <a
                  key={idx}
                  href={asset.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-secondary rounded-md hover:bg-secondary/80 transition-colors"
                >
                  <span className="text-sm font-medium">{asset.name}</span>
                  <ArrowSquareOut size={16} className="text-muted-foreground" />
                </a>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-secondary/50 rounded-md text-center">
              <BookOpen size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No training assets available</p>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );

  const renderDepartmentDetails = () => (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</h4>
        <p className="text-sm text-foreground">{data.description || "No description"}</p>
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">System Touchpoints</h4>
        <div className="flex flex-wrap gap-2">
          {data.system_touchpoints?.map((sys, idx) => (
            <Badge key={idx} variant="secondary" className="bg-red-50 text-red-700">{sys}</Badge>
          ))}
        </div>
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
          <Warning size={14} className="text-destructive" /> Bottlenecks
        </h4>
        {data.bottlenecks?.length > 0 ? (
          <ul className="space-y-2">
            {data.bottlenecks.map((bn, idx) => (
              <li key={idx} className="p-2 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">{bn}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No bottlenecks identified</p>
        )}
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Escalation Paths</h4>
        {data.escalation_paths?.length > 0 ? (
          <div className="space-y-2">
            {data.escalation_paths.map((ep, idx) => (
              <div key={idx} className="p-2 bg-secondary rounded-md text-sm">
                <span className="font-medium">{ep.from}</span> → <span>{ep.to}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No escalation paths defined</p>
        )}
      </div>
    </div>
  );

  const renderProcessDetails = () => (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</h4>
        <p className="text-sm text-foreground">{data.description || "No description"}</p>
      </div>

      {data.estimated_duration && (
        <>
          <Separator />
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Estimated Duration</h4>
            <Badge variant="outline">{data.estimated_duration}</Badge>
          </div>
        </>
      )}

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Process Steps</h4>
        {data.steps?.length > 0 ? (
          <ol className="space-y-3">
            {data.steps.map((step, idx) => (
              <li key={idx} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {step.step}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{step.name}</p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No steps defined</p>
        )}
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Handoffs</h4>
        {data.handoffs?.length > 0 ? (
          <div className="space-y-2">
            {data.handoffs.map((ho, idx) => (
              <div key={idx} className="p-2 bg-secondary rounded-md text-sm">
                <span className="font-medium">{ho.from}</span> → <span>{ho.to}</span>
                <span className="text-muted-foreground ml-2">({ho.what})</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No handoffs defined</p>
        )}
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Approvals Required</h4>
        {data.approvals?.length > 0 ? (
          <div className="space-y-2">
            {data.approvals.map((ap, idx) => (
              <div key={idx} className="p-2 bg-accent/10 border border-accent/20 rounded-md text-sm">
                <span className="font-medium">{ap.name}</span>
                <span className="text-muted-foreground ml-2">by {ap.approver}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No approvals required</p>
        )}
      </div>
    </div>
  );

  const renderSystemDetails = () => (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Type</h4>
        <Badge>{data.type}</Badge>
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</h4>
        <p className="text-sm text-foreground">{data.description || "No description"}</p>
      </div>

      {data.url && data.url !== "#" && (
        <>
          <Separator />
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">System URL</h4>
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-primary hover:underline"
            >
              <ArrowSquareOut size={14} />
              {data.url}
            </a>
          </div>
        </>
      )}

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Used By Roles</h4>
        <div className="flex flex-wrap gap-2">
          {data.used_by_roles?.length > 0 ? (
            data.used_by_roles.map((roleId, idx) => {
              const role = roles.find(r => r.role_id === roleId);
              return <Badge key={idx} variant="outline">{role?.title || roleId}</Badge>;
            })
          ) : (
            <p className="text-sm text-muted-foreground">No roles assigned</p>
          )}
        </div>
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Used In Processes</h4>
        <div className="flex flex-wrap gap-2">
          {data.used_in_processes?.length > 0 ? (
            data.used_in_processes.map((proc, idx) => (
              <Badge key={idx} variant="secondary" className="bg-violet-50 text-violet-700">{proc}</Badge>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No processes assigned</p>
          )}
        </div>
      </div>

      {data.integration_notes && (
        <>
          <Separator />
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Integration Notes</h4>
            <p className="text-sm text-muted-foreground">{data.integration_notes}</p>
          </div>
        </>
      )}
    </div>
  );

  const getIcon = () => {
    switch (type) {
      case "role": return <User size={20} className="text-primary" />;
      case "department": return <Buildings size={20} style={{ color: data.color || "#002FA7" }} />;
      case "process": return <FlowArrow size={20} className="text-violet-600" />;
      case "system": return <Cpu size={20} className="text-red-600" />;
      default: return null;
    }
  };

  const getTitle = () => {
    switch (type) {
      case "role": return data.title;
      case "department": return data.name;
      case "process": return data.name;
      case "system": return data.name;
      default: return "Details";
    }
  };

  const getSubtitle = () => {
    switch (type) {
      case "role": return data.department;
      case "department": return "Department";
      case "process": return "Process Flow";
      case "system": return data.type;
      default: return "";
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden flex flex-col" data-testid="detail-sheet">
        <SheetHeader className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary flex items-center justify-center">
              {type === "role" && data?.photo_url ? (
                <img src={data.photo_url} alt={data.title} className="w-full h-full object-cover" />
              ) : (
                getIcon()
              )}
            </div>
            <div>
              <SheetTitle className="text-left">{getTitle()}</SheetTitle>
              <SheetDescription className="text-left">{getSubtitle()}</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        
        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {type === "role" && renderRoleDetails()}
          {type === "department" && renderDepartmentDetails()}
          {type === "process" && renderProcessDetails()}
          {type === "system" && renderSystemDetails()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
